// frontend/src/components/AdminTeamDetail/ImportRosterModal.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { readRosterWorkbook } from '../../utils/rosterWorkbook';
import { parseRoster } from '../../utils/rosterParser';
import { matchRoster, resolvePersonName } from '../../utils/matchRoster';
import { fetchCandidates, liveSearchFor } from '../../services/rosterMatchService';
import { getCountryByName } from '../../data/countries';

// Import a team's submitted list — the Team LongList or the Accreditation List — in place
// of the old CSV import.
//
// Every import passes through this review. Nothing is written from the drop zone, no
// Sportradar link is ever applied on our own initiative, and the button names the exact
// number of people it will create.
//
// Reads from Sportradar's mirror only. Nothing here writes to Sportradar.

const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');

const FORMAT_LABELS = {
    accreditation: 'Accreditation list',
    longlist: 'Team longlist',
};

const ImportRosterModal = ({ team, existingPlayers = [], onCancel, onImport }) => {
    const [file, setFile] = useState(null);
    const [parsed, setParsed] = useState(null);
    const [matches, setMatches] = useState([]);
    const [links, setLinks] = useState(() => new Map()); // row index -> person_id
    const [selected, setSelected] = useState(() => new Set());
    const [error, setError] = useState(null);
    const [reading, setReading] = useState(false);
    const [matching, setMatching] = useState(false);
    const [liveSearch, setLiveSearch] = useState(null); // { done, total }
    const [importing, setImporting] = useState(false);
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);
    const dialogRef = useRef(null);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !importing) onCancel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onCancel, importing]);

    useEffect(() => { dialogRef.current?.focus(); }, [parsed]);

    const alreadyOnRoster = useMemo(
        () => new Set(existingPlayers.map((p) => norm(p.player_name))),
        [existingPlayers]
    );

    // Names repeated inside the file itself are flagged too, not just ones already saved.
    const rows = useMemo(() => {
        if (!parsed) return [];
        const seen = new Set();
        return parsed.entries.map((entry, i) => {
            const key = norm(entry.fullName);
            const duplicate = alreadyOnRoster.has(key) || seen.has(key);
            seen.add(key);
            return { entry, duplicate, candidates: matches[i]?.candidates || [] };
        });
    }, [parsed, alreadyOnRoster, matches]);

    // One Sportradar person cannot be two players. Whichever row claimed them first keeps
    // them; the rest see the candidate disabled rather than silently duplicated.
    const claimedBy = useMemo(() => {
        const owner = new Map();
        links.forEach((personId, rowIndex) => {
            if (personId && !owner.has(personId)) owner.set(personId, rowIndex);
        });
        return owner;
    }, [links]);

    const handleFile = useCallback(async (chosen) => {
        setError(null);
        setParsed(null);
        setMatches([]);
        setLinks(new Map());
        setFile(chosen);
        setReading(true);
        try {
            const { matrix } = await readRosterWorkbook(chosen);
            const result = parseRoster(matrix);
            if (!result.entries.length) {
                setError(result.warnings[0] || 'No people were found in that file.');
                setFile(null);
                return;
            }
            setParsed(result);

            // Duplicates start unticked: importing them again is the mistake this screen
            // exists to prevent.
            const known = new Set(existingPlayers.map((p) => norm(p.player_name)));
            const seen = new Set();
            const fresh = new Set();
            result.entries.forEach((entry, i) => {
                const key = norm(entry.fullName);
                if (!known.has(key) && !seen.has(key)) fresh.add(i);
                seen.add(key);
            });
            setSelected(fresh);

            setMatching(true);
            const { candidates } = await fetchCandidates(result.entries);
            const fromMirror = matchRoster(result.entries, candidates);
            setMatches(fromMirror);

            // The mirror is refreshed on the 1st and 15th, so anyone registered since is
            // absent from it — which is precisely the population being entered for an
            // event that is being imported now. Ask Sportradar directly about the rows it
            // could not place, and only those.
            const unplaced = fromMirror
                .map((row, i) => ({ i, entry: row.entry }))
                .filter(({ i }) => !fromMirror[i].candidates.length);

            if (unplaced.length) {
                setLiveSearch({ done: 0, total: unplaced.length });
                const live = await liveSearchFor(
                    unplaced.map((u) => u.entry),
                    { onProgress: (done, total) => setLiveSearch({ done, total }) }
                );
                if (live.length) {
                    const rescored = matchRoster(unplaced.map((u) => u.entry), live);
                    const merged = [...fromMirror];
                    unplaced.forEach((u, k) => {
                        if (rescored[k].candidates.length) merged[u.i] = rescored[k];
                    });
                    setMatches(merged);
                }
                setLiveSearch(null);
            }
        } catch (e) {
            setError(e.message);
            setFile(null);
        } finally {
            setReading(false);
            setMatching(false);
        }
    }, [existingPlayers]);

    const toggle = (i) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i); else next.add(i);
        return next;
    });

    const setLink = (rowIndex, personId) => setLinks((prev) => {
        const next = new Map(prev);
        if (!personId) next.delete(rowIndex); else next.set(rowIndex, personId);
        return next;
    });

    const allSelected = selected.size === rows.length && rows.length > 0;
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((_, i) => i)));

    // Bulk convenience, still an explicit act: it links only what the scorer graded
    // strong, and every one stays individually reversible before importing.
    const strongUnlinked = rows
        .map((row, i) => ({ i, top: row.candidates[0] }))
        .filter(({ i, top }) => top?.tier === 'strong' && !links.has(i) && !claimedBy.has(top.person.person_id));

    const linkAllStrong = () => setLinks((prev) => {
        const next = new Map(prev);
        const taken = new Set([...prev.values()]);
        for (const { i, top } of strongUnlinked) {
            if (taken.has(top.person.person_id)) continue;
            next.set(i, top.person.person_id);
            taken.add(top.person.person_id);
        }
        return next;
    });

    /** The person a row is linked to, or null. */
    const linkedPerson = (row, i) => {
        const id = links.get(i);
        return id ? row.candidates.find((c) => c.person.person_id === id)?.person || null : null;
    };

    const runImport = async () => {
        if (!selected.size) return;
        setImporting(true);
        try {
            const iso3 = getCountryByName(team?.name)?.iso3 || null;
            const payload = [...selected].sort((a, b) => a - b).map((i) => {
                const row = rows[i];
                const person = linkedPerson(row, i);
                const srName = person ? resolvePersonName(person) : null;
                return {
                    // A confirmed link makes Sportradar the source of the stored name.
                    name: srName?.fullName || row.entry.fullName,
                    first_name: srName ? srName.firstName : row.entry.givenName,
                    last_name: srName ? srName.lastName : row.entry.familyName,
                    dob: row.entry.dob,
                    nationality: iso3,
                    person_id: person?.person_id || null,
                    // Carried through so the photo can be copied straight from the CDN,
                    // without asking Sportradar's API to resolve it again.
                    sportradar_photo_url: person?.current_photo_url || null,
                };
            });
            await onImport(payload);
        } finally {
            setImporting(false);
        }
    };

    const reset = () => {
        setFile(null); setParsed(null); setMatches([]);
        setLinks(new Map()); setSelected(new Set()); setError(null);
    };

    const linkedCount = [...selected].filter((i) => links.has(i)).length;

    return (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div
                ref={dialogRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-label="Import team list"
                className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col outline-none"
            >
                <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-gray-200">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Import team list</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {team?.name} · reads the Team LongList or Accreditation List as submitted
                        </p>
                    </div>
                    <button
                        onClick={onCancel}
                        disabled={importing}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-40 rounded-lg p-1"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Step 1 — choose a file */}
                {!parsed && (
                    <div className="p-6">
                        <label
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
                            }}
                            className={`flex flex-col items-center justify-center gap-3 px-6 py-12 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                                dragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                            }`}
                        >
                            {reading ? (
                                <>
                                    <svg className="w-8 h-8 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                    </svg>
                                    <p className="text-sm text-gray-600">Reading {file?.name}…</p>
                                </>
                            ) : (
                                <>
                                    <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-gray-700 font-medium">Drop the team's list here</p>
                                    <p className="text-sm text-gray-500">or click to choose a file · .xlsx or .csv</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Banner rows and logos are fine — the columns are found automatically.
                                    </p>
                                </>
                            )}
                            <input
                                ref={inputRef}
                                type="file"
                                accept=".xlsx,.xls,.csv,.txt"
                                className="hidden"
                                onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = null; }}
                            />
                        </label>

                        {error && (
                            <div className="mt-4 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                <svg className="w-5 h-5 shrink-0 mt-px" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 2 — review */}
                {parsed && (
                    <>
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 space-y-3">
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 font-semibold text-xs">
                                    {FORMAT_LABELS[parsed.format] || 'Roster'}
                                </span>
                                <span className="text-gray-600">
                                    {parsed.entries.length} {parsed.entries.length === 1 ? 'person' : 'people'} found
                                </span>
                                {parsed.skippedRows > 0 && (
                                    <span className="text-gray-400">· {parsed.skippedRows} empty {parsed.skippedRows === 1 ? 'row' : 'rows'} skipped</span>
                                )}
                                <button onClick={reset} className="ml-auto text-indigo-600 hover:text-indigo-800 font-medium">
                                    Choose a different file
                                </button>
                            </div>

                            {parsed.warnings.length > 0 && (
                                <details className="text-sm">
                                    <summary className="cursor-pointer text-amber-700 font-medium">
                                        {parsed.warnings.length} {parsed.warnings.length === 1 ? 'thing' : 'things'} to check
                                    </summary>
                                    <ul className="mt-2 space-y-1 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                        {parsed.warnings.map((w, i) => <li key={i}>· {w}</li>)}
                                    </ul>
                                </details>
                            )}

                            <div className="flex items-center gap-3 text-sm">
                                {matching ? (
                                    <span className="flex items-center gap-2 text-gray-500">
                                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                        </svg>
                                        {liveSearch
                                            ? `Asking Sportradar about ${liveSearch.total} we could not place… ${liveSearch.done}/${liveSearch.total}`
                                            : 'Looking for these people in Sportradar…'}
                                    </span>
                                ) : (
                                    <>
                                        <span className="text-gray-600">
                                            {linkedCount} of {selected.size} selected will be linked to Sportradar
                                        </span>
                                        {strongUnlinked.length > 0 && (
                                            <button
                                                onClick={linkAllStrong}
                                                className="px-3 py-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                                            >
                                                Link {strongUnlinked.length} confident {strongUnlinked.length === 1 ? 'match' : 'matches'}
                                            </button>
                                        )}
                                        {links.size > 0 && (
                                            <button
                                                onClick={() => setLinks(new Map())}
                                                className="px-3 py-1 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700"
                                            >
                                                Unlink all {links.size}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Rows */}
                        <div className="flex-1 overflow-y-auto">
                            <div className="sticky top-0 z-10 flex items-center gap-3 px-6 py-2 bg-white border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    onChange={toggleAll}
                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    aria-label="Select all"
                                />
                                <span className="w-8 text-right tabular-nums">Row</span>
                                <span className="flex-1">From the file</span>
                                <span className="w-[46%]">Sportradar</span>
                            </div>

                            {rows.map((row, i) => {
                                const { entry, duplicate, candidates } = row;
                                const checked = selected.has(i);
                                const person = linkedPerson(row, i);
                                const srName = person ? resolvePersonName(person) : null;
                                const top = candidates[0];

                                return (
                                    <div
                                        key={`${entry.sourceRow}-${entry.fullName}`}
                                        className={`flex items-center gap-3 px-6 py-2.5 border-b border-gray-100 ${
                                            checked ? 'bg-white' : 'bg-gray-50/60'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => toggle(i)}
                                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            aria-label={`Import ${entry.fullName}`}
                                        />
                                        {/* The sheet's own row number, so anything odd can be found in Excel. */}
                                        <span className="w-8 text-right text-xs text-gray-400 font-mono tabular-nums">{entry.sourceRow}</span>

                                        <div className="flex-1 min-w-0">
                                            <div className={`font-medium truncate ${checked ? 'text-gray-900' : 'text-gray-500'}`}>
                                                {entry.fullName}
                                                {duplicate && (
                                                    <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[11px] font-semibold align-middle">
                                                        already on roster
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                {entry.dob
                                                    ? <span className={entry.dobAmbiguous ? 'text-amber-700' : ''}>{entry.dob}{entry.dobAmbiguous && ' ?'}</span>
                                                    : <span className="text-amber-600">no date of birth</span>}
                                                {entry.designation && <span> · {entry.designation}</span>}
                                            </div>
                                        </div>

                                        {/* Sportradar side */}
                                        <div className="w-[46%]">
                                            {matching ? (
                                                <span className="flex items-center gap-2 text-xs text-gray-400">
                                                    <span className="w-8 h-8 rounded bg-gray-100 animate-pulse shrink-0" />
                                                    Searching…
                                                </span>
                                            ) : !top ? (
                                                <span className="text-xs text-gray-400">Not found in Sportradar</span>
                                            ) : (
                                                <CandidatePicker
                                                    candidates={candidates}
                                                    linkedId={links.get(i) || ''}
                                                    claimedBy={claimedBy}
                                                    rowIndex={i}
                                                    onChange={(personId) => setLink(i, personId)}
                                                />
                                            )}
                                            {srName && (
                                                <p className="mt-1 text-[11px] text-emerald-700">
                                                    Will be saved as <span className="font-semibold">{srName.fullName}</span>
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-gray-200 bg-white rounded-b-xl">
                            <p className="text-sm text-gray-600">
                                <span className="font-semibold text-gray-900">{selected.size}</span> of {rows.length} selected
                                {linkedCount > 0 && <span className="text-emerald-700"> · {linkedCount} linked</span>}
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={onCancel}
                                    disabled={importing}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={runImport}
                                    disabled={!selected.size || importing}
                                    className="px-5 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {importing && (
                                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                        </svg>
                                    )}
                                    {importing ? 'Importing…' : `Import ${selected.size} ${selected.size === 1 ? 'person' : 'people'}`}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const TIER_STYLE = {
    strong: 'bg-emerald-100 text-emerald-800',
    review: 'bg-amber-100 text-amber-800',
};

/**
 * One row's Sportradar candidates.
 *
 * Nothing is preselected. A confident match still has to be linked, either individually
 * here or through the bulk action, because a wrong link puts the wrong face on an
 * accreditation badge.
 */
const CandidatePicker = ({ candidates, linkedId, claimedBy, rowIndex, onChange }) => {
    const active = candidates.find((c) => c.person.person_id === linkedId);
    const shown = active || candidates[0];
    const person = shown.person;
    const takenElsewhere = claimedBy.has(person.person_id) && claimedBy.get(person.person_id) !== rowIndex;

    return (
        <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-gray-100 overflow-hidden shrink-0">
                {person.current_photo_url && (
                    <img
                        src={person.current_photo_url}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                )}
            </div>

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    <span className="text-sm text-gray-800 truncate">
                        {person.name_full_latin || person.name_full_local}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${TIER_STYLE[shown.tier]}`}>
                        {shown.tier === 'strong' ? 'confident' : 'check'}
                    </span>
                </div>
                <div className="text-[11px] text-gray-400">
                    {person.dob || 'no date'}
                    {shown.dobStatus === 'conflict' && <span className="text-red-600 font-semibold"> · date differs</span>}
                    {person.nationality && <span> · {person.nationality}</span>}
                    {/* Absent from the mirror, so newer than the last sync. */}
                    {person.fromLiveSearch && <span className="text-indigo-600"> · just added to Sportradar</span>}
                    {candidates.length > 1 && (
                        <select
                            value={linkedId}
                            onChange={(e) => onChange(e.target.value)}
                            className="ml-2 text-[11px] border border-gray-200 rounded bg-white"
                            aria-label="Choose a different Sportradar person"
                        >
                            <option value="">choose…</option>
                            {candidates.map((c) => (
                                <option key={c.person.person_id} value={c.person.person_id}>
                                    {c.person.name_full_latin || c.person.name_full_local} ({Math.round(c.score * 100)}%)
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            <button
                type="button"
                onClick={() => onChange(linkedId ? '' : person.person_id)}
                disabled={takenElsewhere && !linkedId}
                title={takenElsewhere && !linkedId ? 'Already linked to another row' : undefined}
                className={`group px-2 py-1 rounded text-xs font-semibold shrink-0 w-16 text-center disabled:opacity-40 disabled:cursor-not-allowed ${
                    linkedId
                        // Green while linked, red on hover: the click undoes it, and the
                        // colour should say so before it happens.
                        ? 'bg-emerald-600 text-white hover:bg-red-600'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
            >
                {linkedId ? (
                    <>
                        <span className="group-hover:hidden">Linked</span>
                        <span className="hidden group-hover:inline">Unlink</span>
                    </>
                ) : 'Link'}
            </button>
        </div>
    );
};

export default ImportRosterModal;
